import type { CustomerPenalty } from '@/types/api'

/**
 * Turning a penalty row into the words an operator would actually use.
 *
 * Kept out of the page and the drawer because both need it: the table shows the
 * short form under the amount, and the details view reads the same facts back
 * in a sentence.
 */

/** "Cairo · 50% refund" — the rule that produced the charge, not the enum. */
export function describeBand(penalty: CustomerPenalty): string {
  const service = penalty.serviceType === 'TRAVEL' ? 'Out-of-Cairo' : 'Cairo'
  switch (penalty.policyBand) {
    case 'NONE':
      return `${service} · no refund`
    case 'HALF':
      return `${service} · 50% refund`
    case 'FULL':
      return `${service} · full refund`
    default:
      return service
  }
}

/**
 * How close to pickup the cancellation was.
 *
 * This is the fact customers dispute, so it is shown wherever the amount is.
 */
export function describeWindow(penalty: CustomerPenalty): string {
  const minutes = penalty.minutesBeforePickup
  if (minutes === null || minutes === undefined) return 'Pickup time not recorded'
  if (minutes < 0) return `${formatDuration(-minutes)} after pickup`
  return `${formatDuration(minutes)} before pickup`
}

/** What the status means for the customer's balance, in a few words. */
export function describeStatus(penalty: CustomerPenalty): string | undefined {
  switch (penalty.status) {
    case 'PENDING_SETTLEMENT':
      return penalty.settledBookingReference
        ? `Awaiting payment of ${penalty.settledBookingReference}`
        : 'Awaiting payment'
    case 'SETTLED':
      return penalty.settledBookingReference ? `Paid with ${penalty.settledBookingReference}` : 'Paid'
    case 'WAIVED':
      return penalty.waivedByAdminName ? `By ${penalty.waivedByAdminName}` : 'Waived'
    default:
      return 'Added to the next booking'
  }
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} min`
  if (rest === 0) return `${hours}h`
  return `${hours}h ${rest}m`
}
